import { useRef, useEffect, useState, useCallback } from "react";
import { Camera, RotateCcw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PhotoCaptureProps {
    onCapture: (dataUrl: string) => void;
    onCancel: () => void;
    /**
     * The caller is still saving this photo. Disables Use Photo / Retake and
     * says "Saving…", because on a slow uplink the dialog stays open for
     * seconds and a second tap is a second upload.
     */
    saving?: boolean;
}

export function PhotoCapture({ onCapture, onCancel, saving }: PhotoCaptureProps) {
    // Driven ONLY by the caller's real mutation state. An internal "submitted"
    // latch looks equivalent and is not: `Dialog` renders its children
    // unconditionally (it only shows/hides the native <dialog>), so this
    // component stays mounted for the life of the screen and a latch never
    // resets — the camera would work exactly once per job, reopening on the
    // next device with the previous device's frozen preview.
    const busy = saving === true;
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");

    // Fallback when getUserMedia is denied/unavailable (common on locked-down
    // iOS profiles): let the tech pick from their photo library / system
    // camera. Reads to the same dataURL shape `takePhoto` emits.
    const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") setPreview(reader.result);
        };
        reader.readAsDataURL(file);
        // Allow re-picking the same file after a retake.
        e.target.value = "";
    };

    const startCamera = useCallback(
        async (facing: "environment" | "user") => {
            try {
                if (stream) {
                    stream.getTracks().forEach((t) => t.stop());
                }
                const ms = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
                    audio: false,
                });
                setStream(ms);
                setError(null);
                if (videoRef.current) {
                    videoRef.current.srcObject = ms;
                }
            } catch {
                setError("Camera unavailable. Please allow camera access.");
            }
        },
        [stream],
    );

    useEffect(() => {
        startCamera(facingMode);
        return () => {
            stream?.getTracks().forEach((t) => t.stop());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const takePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d")?.drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setPreview(dataUrl);
    };

    const flipCamera = async () => {
        const next = facingMode === "environment" ? "user" : "environment";
        setFacingMode(next);
        await startCamera(next);
    };

    if (preview) {
        return (
            <div className="space-y-3">
                <img src={preview} alt="Preview" className="w-full rounded-lg" />
                <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" disabled={busy} onClick={() => setPreview(null)}>
                        Retake
                    </Button>
                    <Button
                        className="flex-1"
                        disabled={busy}
                        onClick={() => {
                            // A slow uplink leaves this dialog open for seconds;
                            // without a busy state a green hire taps again and
                            // every tap is another upload. Clearing the preview
                            // also means the next device opens on a live camera
                            // rather than the last device's picture.
                            const shot = preview;
                            setPreview(null);
                            onCapture(shot);
                        }}
                    >
                        {busy ? "Saving…" : "Use Photo"}
                    </Button>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8 space-y-3">
                <p className="text-sm text-foreground/80">{error}</p>
                <p className="text-xs text-foreground/60">You can still upload a photo from your library instead.</p>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleFilePick}
                />
                <div className="flex gap-2 justify-center">
                    <Button variant="outline" onClick={onCancel}>
                        Close
                    </Button>
                    <Button className="gap-2" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="h-4 w-4" /> Upload photo
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={onCancel}>
                    Cancel
                </Button>
                <Button variant="outline" size="icon" onClick={flipCamera}>
                    <RotateCcw className="h-4 w-4" />
                </Button>
                <Button className="flex-1 gap-2" onClick={takePhoto}>
                    <Camera className="h-4 w-4" /> Capture
                </Button>
            </div>
        </div>
    );
}
